import { useState, type KeyboardEvent, type MouseEvent } from "react";
import {
  Draggable,
  type DraggableProvided,
  type DraggableProvidedDraggableProps,
} from "@hello-pangea/dnd";
import { useNavigate } from "react-router";
import { useDelete, useNotify, useRefresh } from "ra-core";
import {
  GripVertical,
  History,
  Mail,
  MessageSquare,
  Pencil,
  Phone,
  PhoneCall,
  Trash2,
  Trophy,
  XCircle,
} from "lucide-react";

import { Confirm } from "@/components/admin/confirm";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

import { IconButton } from "@/components/ui/icon-button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { Contact } from "@/components/atomic-crm/types";
import { MoneyText } from "@/lib/permissions/MoneyText";
import { useCanViewAmounts } from "@/lib/permissions/useMaskedAmount";
import { getLeadKanbanShowPath } from "@/app/routing";
import { LeadActivitySheet } from "@/modules/leads/LeadActivitySheet";
import { LeadEditDialog } from "@/modules/leads/LeadEditDialog";
import { assignedMemberIdsFromContact } from "@/modules/leads/leadAssignments";
import { useLeadKanbanContext } from "@/modules/leads/LeadKanbanContext";
import {
  formatLeadActivityFooter,
  getLeadDisplayName,
  getLeadInitials,
  getLeadPrimaryEmail,
  getLeadPrimaryPhoneDisplay,
  getLeadSourceIcon,
  getMemberAccentColor,
  getMemberInitials,
  getMemberDisplayName,
  isLeadActivityStale,
  resolveLeadLastActivity,
  shouldShowLeadEmail,
  shouldShowLeadQuoteAmount,
} from "@/modules/leads/leadCardUtils";
import { normalizeLeadStage } from "@/modules/leads/leadStages";
import { isLeadTerminalStage } from "@/modules/leads/leadFollowUpUtils";
import { useMessagesQuickAccessOptional } from "@/modules/messages/messagesQuickAccessContext";
import { contactHasSmsPhone } from "@/modules/messages/messageContactUtils";
import { useCrmPhoneCall } from "@/modules/voice/useCrmPhoneCall";
import { BoardCardStrip, boardCardClassName } from "@/modules/shared/board";
import { toneClass } from "@/modules/shared/status";

const cardActionClassName = (isDragging: boolean) =>
  cn(
    "grid size-6 place-items-center rounded-md text-muted-foreground opacity-0 transition-opacity",
    "group-hover:opacity-100 focus-visible:opacity-100",
    "hover:bg-accent hover:text-foreground",
    isDragging && "opacity-100",
  );

const cardToolbarIconClassName =
  "size-7 shrink-0 rounded-none text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-40";

export type LeadCardProps = {
  lead: Contact;
  index: number;
  layout?: "board" | "sidebar";
  selected?: boolean;
};

export const LeadCard = ({
  lead,
  index,
  layout = "board",
  selected = false,
}: LeadCardProps) => {
  const [historyOpen, setHistoryOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const navigate = useNavigate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [deleteOne, { isPending: isDeleting }] = useDelete<Contact>();
  const { membersById, conversationsByContactId, requestStageChange } =
    useLeadKanbanContext();
  const messagesQuickAccess = useMessagesQuickAccessOptional();
  const canViewAmounts = useCanViewAmounts();

  const stage = normalizeLeadStage(lead.lead_stage);
  const isTerminal = isLeadTerminalStage(lead.lead_stage);
  const name = getLeadDisplayName(lead);
  const phone = getLeadPrimaryPhoneDisplay(lead);
  const email = getLeadPrimaryEmail(lead);
  const source = getLeadSourceIcon(lead.lead_source);
  const SourceIcon = source.Icon;
  const assigneeIds = assignedMemberIdsFromContact(lead);
  const assignees = assigneeIds
    .map((id) => membersById[String(id)])
    .filter((member): member is NonNullable<typeof member> => Boolean(member));
  const conversation = conversationsByContactId[String(lead.id)];
  const activity = resolveLeadLastActivity(lead, conversation);
  const activityStale = isLeadActivityStale(activity.at);
  const showEmail = shouldShowLeadEmail(lead);
  const showQuote = shouldShowLeadQuoteAmount(lead);
  const { canCall, voiceReady, callPhone } = useCrmPhoneCall();
  const canCallPhone = Boolean(phone?.e164 && canCall && voiceReady);

  const handleDelete = () => {
    deleteOne(
      "contacts",
      { id: lead.id, previousData: lead },
      {
        onSuccess: () => {
          notify("Lead deleted", { type: "info" });
          refresh();
          setDeleteOpen(false);
        },
        onError: () => {
          notify("Failed to delete lead", { type: "error" });
        },
      },
    );
  };

  const stopCardNavigation = (event: MouseEvent) => {
    event.stopPropagation();
  };

  const stopCardAction = (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
  };

  const stopDragHandleClick = (event: MouseEvent) => {
    event.stopPropagation();
  };

  const openSms = async (event: MouseEvent) => {
    stopCardAction(event);
    if (!contactHasSmsPhone(lead)) {
      notify("This lead has no valid phone number for SMS", {
        type: "warning",
      });
      return;
    }
    if (messagesQuickAccess) {
      await messagesQuickAccess.openSms(lead);
      return;
    }
    notify("Open Messages to text this lead", { type: "info" });
  };

  const openLead = (isDragging = false) => {
    if (isDragging) return;
    navigate(getLeadKanbanShowPath(lead.id, stage));
  };

  const renderCard = ({
    isDragging,
    dragHandleProps,
    draggableProps,
    innerRef,
  }: {
    isDragging: boolean;
    dragHandleProps?: DraggableProvided["dragHandleProps"];
    draggableProps?: DraggableProvidedDraggableProps;
    innerRef?: DraggableProvided["innerRef"];
  }) => (
    <div ref={innerRef} {...draggableProps}>
      <Card
        role="button"
        tabIndex={0}
        onClick={() => openLead(isDragging)}
        onKeyDown={(event: KeyboardEvent) => {
          if (event.key === "Enter" || event.key === "") {
            event.preventDefault();
            openLead(isDragging);
          }
        }}
        className={cn(
          "group cursor-pointer gap-0 p-0",
          boardCardClassName(isDragging),
          layout === "sidebar" && selected && "border-primary bg-primary/5",
        )}
      >
        <div className="absolute right-0.5 top-0.5 z-10 flex flex-col gap-0.5">
          {layout === "board" && dragHandleProps ? (
            <div
              {...dragHandleProps}
              onClick={stopDragHandleClick}
              onMouseDown={stopDragHandleClick}
              className={cn(
                cardActionClassName(isDragging),
                "cursor-grab active:cursor-grabbing",
              )}
              aria-label="Drag lead"
              role="button"
              tabIndex={0}
            >
              <GripVertical className="size-3.5" />
            </div>
          ) : null}
          <IconButton
            aria-label="Activity history"
            className={cn(cardActionClassName(isDragging), "size-6")}
            title="Activity history"
            onClick={(event) => {
              stopCardAction(event);
              setHistoryOpen(true);
            }}
          >
            <History className="size-3.5" />
            <span className="sr-only">Activity history</span>
          </IconButton>
          <IconButton
            aria-label="Delete lead"
            className={cn(
              cardActionClassName(isDragging),
              "size-6 hover:text-destructive",
            )}
            title="Delete lead"
            disabled={isDeleting}
            onClick={(event) => {
              stopCardAction(event);
              setDeleteOpen(true);
            }}
          >
            <Trash2 className="size-3.5" />
            <span className="sr-only">Delete lead</span>
          </IconButton>
          <IconButton
            aria-label="Edit lead"
            className={cn(cardActionClassName(isDragging), "size-6")}
            title="Edit lead"
            onClick={(event) => {
              stopCardAction(event);
              setEditOpen(true);
            }}
          >
            <Pencil className="size-3.5" />
            <span className="sr-only">Edit lead</span>
          </IconButton>
        </div>

        <div className="p-2.5 pr-7">
          <div className="flex items-start gap-2">
            <Avatar className="size-[34px] shrink-0">
              <AvatarFallback className="bg-muted text-2xs font-semibold">
                {getLeadInitials(lead)}
              </AvatarFallback>
            </Avatar>

            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-1">
                <p className="min-w-0 truncate text-sm leading-snug group-hover:underline">
                  {name}
                </p>
                <span title={source.label} className="shrink-0">
                  <SourceIcon
                    className="size-3.5 text-muted-foreground"
                    aria-label={source.label}
                  />
                </span>
              </div>
              {lead.interested_service ? (
                <p className="mt-0.5 truncate text-2xs leading-snug text-muted-foreground">
                  {lead.interested_service}
                </p>
              ) : null}
            </div>
          </div>

          <div className="mt-1.5 space-y-0.5">
            {phone ? (
              <div className="flex items-center gap-1.5 text-2xs leading-snug text-muted-foreground">
                <Phone className="size-3 shrink-0" />
                <span className="truncate">{phone.display}</span>
              </div>
            ) : null}

            {showEmail && email ? (
              <div className="flex items-center gap-1.5 text-2xs leading-snug text-muted-foreground">
                <Mail className="size-3 shrink-0" />
                <span className="truncate">{email}</span>
              </div>
            ) : null}

            {showQuote && canViewAmounts ? (
              <div className="text-xs font-medium leading-snug text-foreground">
                <MoneyText value={lead.lead_value_estimate} compact />
              </div>
            ) : null}

            <div className="flex items-center gap-1.5">
              {assignees.length > 0 ? (
                <>
                  <div className="flex -space-x-1">
                    {assignees.slice(0, 3).map((assignee) => (
                      <Avatar
                        key={assignee.id}
                        className="size-5 ring-1 ring-background"
                      >
                        <AvatarFallback
                          className="text-3xs font-semibold text-white"
                          style={{
                            backgroundColor: getMemberAccentColor(assignee.id),
                          }}
                        >
                          {getMemberInitials(assignee)}
                        </AvatarFallback>
                      </Avatar>
                    ))}
                  </div>
                  <span className="truncate text-2xs leading-snug text-muted-foreground">
                    {assignees.length === 1
                      ? getMemberDisplayName(assignees[0]!)
                      : `${getMemberDisplayName(assignees[0]!)} +${assignees.length - 1}`}
                  </span>
                </>
              ) : (
                <span className="text-2xs leading-snug text-muted-foreground">
                  Unassigned
                </span>
              )}
            </div>
          </div>
        </div>

        {stage !== "new" ? (
          <BoardCardStrip
            signals={[
              {
                key: "activity",
                label: formatLeadActivityFooter(activity),
                dotClassName: toneClass(
                  activityStale ? "warning" : "muted",
                  "dot",
                ),
              },
            ]}
          />
        ) : null}

        <div>
          {!isTerminal ? (
            <div
              className="flex items-stretch border-t border-border"
              onClick={stopCardNavigation}
              onPointerDown={stopCardNavigation}
            >
              <IconButton
                aria-label="Call"
                className={cn(cardToolbarIconClassName, "flex-1 rounded-bl-lg")}
                title="Call"
                disabled={!canCallPhone}
                onClick={(event) => {
                  stopCardAction(event);
                  if (phone?.e164) {
                    void callPhone({
                      to: phone.e164,
                      contactId: lead.id,
                    });
                  }
                }}
              >
                <PhoneCall className="size-3.5" />
                <span className="sr-only">Call</span>
              </IconButton>
              <IconButton
                aria-label="SMS"
                className={cn(
                  cardToolbarIconClassName,
                  "flex-1 border-l border-border",
                )}
                title="SMS"
                disabled={!contactHasSmsPhone(lead)}
                onClick={openSms}
              >
                <MessageSquare className="size-3.5" />
                <span className="sr-only">SMS</span>
              </IconButton>
              <IconButton
                aria-label="Mark as won"
                className={cn(
                  cardToolbarIconClassName,
                  "flex-1 border-l border-border text-success-text hover:text-success-text",
                )}
                title="Mark as won"
                onClick={(event) => {
                  stopCardAction(event);
                  requestStageChange(lead, "won");
                }}
              >
                <Trophy className="size-3.5" />
                <span className="sr-only">Mark as won</span>
              </IconButton>
              <IconButton
                aria-label="Mark as lost"
                className={cn(
                  cardToolbarIconClassName,
                  "flex-1 rounded-br-lg border-l border-border text-destructive hover:text-destructive",
                )}
                title="Mark as lost"
                onClick={(event) => {
                  stopCardAction(event);
                  requestStageChange(lead, "lost");
                }}
              >
                <XCircle className="size-3.5" />
                <span className="sr-only">Mark as lost</span>
              </IconButton>
            </div>
          ) : null}
        </div>

        <LeadActivitySheet
          lead={lead}
          open={historyOpen}
          onOpenChange={setHistoryOpen}
        />
        <LeadEditDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          contactId={lead.id}
        />
        <Confirm
          isOpen={deleteOpen}
          title="Delete this lead? "
          content="This action cannot be undone."
          confirm="Delete"
          confirmColor="warning"
          onConfirm={handleDelete}
          onClose={() => setDeleteOpen(false)}
          loading={isDeleting}
        />
      </Card>
    </div>
  );

  if (layout === "sidebar") {
    return renderCard({ isDragging: false });
  }

  return (
    <Draggable draggableId={String(lead.id)} index={index}>
      {(provided, snapshot) =>
        renderCard({
          isDragging: snapshot.isDragging,
          dragHandleProps: provided.dragHandleProps,
          draggableProps: provided.draggableProps,
          innerRef: provided.innerRef,
        })
      }
    </Draggable>
  );
};
